"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface AccordionContextType {
    openValue: string | null;
    setOpenValue: (value: string | null) => void;
    collapsible: boolean;
}

const AccordionContext = React.createContext<AccordionContextType | undefined>(undefined);

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
    type?: "single";
    collapsible?: boolean;
    defaultValue?: string;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
    ({ className, children, defaultValue, collapsible = false, ...props }, ref) => {
        const [openValue, setOpenValue] = React.useState<string | null>(defaultValue || null);

        return (
            <AccordionContext.Provider value={{ openValue, setOpenValue, collapsible }}>
                <div ref={ref} className={cn("w-full", className)} {...props}>
                    {children}
                </div>
            </AccordionContext.Provider>
        );
    }
);
Accordion.displayName = "Accordion";

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
    value: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
    ({ className, value, children, ...props }, ref) => {
        return (
            <div ref={ref} className={cn("border-b", className)} data-value={value} {...props}>
                {children}
            </div>
        );
    }
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { }

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
    ({ className, children, ...props }, ref) => {
        const context = React.useContext(AccordionContext);
        const parentValue = (ref as any)?.current?.closest('[data-value]')?.getAttribute('data-value');

        const buttonRef = React.useRef<HTMLButtonElement>(null);
        React.useImperativeHandle(ref, () => buttonRef.current!);

        const [itemValue, setItemValue] = React.useState<string | null>(null);

        React.useEffect(() => {
            const value = buttonRef.current?.closest('[data-value]')?.getAttribute('data-value');
            setItemValue(value || null);
        }, []);

        if (!context) return null;

        const isOpen = context.openValue === itemValue;

        const handleClick = () => {
            if (context.collapsible && isOpen) {
                context.setOpenValue(null);
            } else {
                context.setOpenValue(itemValue);
            }
        };

        return (
            <h3 className="flex">
                <button
                    ref={buttonRef}
                    className={cn(
                        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
                        className
                    )}
                    type="button"
                    aria-expanded={isOpen}
                    onClick={handleClick}
                    {...props}
                >
                    {children}
                    <ChevronDown
                        aria-hidden="true"
                        className={cn(
                            "h-4 w-4 shrink-0 transition-transform duration-200",
                            isOpen && "rotate-180"
                        )}
                    />
                </button>
            </h3>
        );
    }
);
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> { }

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
    ({ className, children, ...props }, ref) => {
        const context = React.useContext(AccordionContext);
        const contentRef = React.useRef<HTMLDivElement>(null);
        React.useImperativeHandle(ref, () => contentRef.current!);

        const [itemValue, setItemValue] = React.useState<string | null>(null);

        React.useEffect(() => {
            const value = contentRef.current?.closest('[data-value]')?.getAttribute('data-value');
            setItemValue(value || null);
        }, []);

        if (!context) return null;

        const isOpen = context.openValue === itemValue;

        return (
            <div
                ref={contentRef}
                className={cn(
                    "overflow-hidden text-sm transition-all duration-300",
                    isOpen ? "animate-accordion-down" : "animate-accordion-up"
                )}
                aria-hidden={!isOpen}
                style={{
                    maxHeight: isOpen ? "1000px" : "0",
                    opacity: isOpen ? 1 : 0,
                }}
                {...props}
            >
                <div className={cn("pb-4 pt-0", className)}>{children}</div>
            </div>
        );
    }
);

AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };